import { CompactText } from 'ui/CompactText';
import { ProfileChangeList, type ProfileChangeRow } from 'ui/ProfileChangeList';
import { useQuery } from '@tanstack/react-query';

import { api, type ApiRequestConfig } from 'api/v1/api';
import { isAuthSessionActive, useAuthSession } from 'hooks/useAuthSession';
import { useVerificationEdits } from 'hooks/useVerificationEdits';
import { StatusChip } from 'ui/kit/Kit';
import { StepBody } from 'ui/kit/uiMotion';

import type { TeacherEditFormValues } from '../types';
import classes from './TutorQuestionnaire.module.scss';

export type TutorStep = {
  key: string;
  title: string;
  done: boolean;
};

type Moderation = {
  status: string;
  label: string;
};

type Readiness = {
  steps?: TutorStep[];
  ready_to_submit?: boolean;
  moderation?: Moderation;
  message?: string;
};

const FALLBACK: TutorStep[] = [
  { key: 'general', title: 'Основное', done: false },
  { key: 'education', title: 'Образование', done: false },
  { key: 'services', title: 'Услуги и цены', done: false },
  { key: 'photo', title: 'Фото', done: false },
  { key: 'review', title: 'Проверка', done: false },
];

const TAB: Record<string, string> = {
  general: 'Основное',
  education: 'Образование',
  services: 'Услуги и цены',
  photo: 'Фото',
  review: 'Проверка',
};

const FACT: Record<string, string> = {
  pending_review: 'Анкета на проверке. Поиск заявок и чаты откроются, когда профиль одобрят.',
  approved: 'Профиль одобрен.',
  changes_requested: 'В анкете нужны правки. Исправьте их и отправьте снова.',
  rejected: 'В анкете нужны правки. Исправьте их и отправьте снова.',
};

export function useTutorQuestionnaire() {
  const { data: session } = useAuthSession();
  const verification = useVerificationEdits();
  const readiness = useQuery<Readiness | null>({
    queryKey: ['tutor-profile-readiness'],
    queryFn: async () => {
      try {
        const response = await api.get('/profile/tutor/readiness', {
          skipGlobalErrorHandler: true,
        } as ApiRequestConfig);
        return response.data as Readiness;
      } catch {
        return null;
      }
    },
    enabled: isAuthSessionActive(session),
    staleTime: 30_000,
    retry: false,
  });

  const fromStatus = verification.data?.questionnaire;
  const rawSteps = readiness.data?.steps?.length
    ? readiness.data.steps
    : fromStatus?.steps?.length
      ? fromStatus.steps
      : FALLBACK;
  // p7: шаг price слит с services (одни и те же поля). Старый ответ API - сворачиваем в один шаг.
  const priceStep = rawSteps.find((s) => s.key === 'price');
  const steps = rawSteps
    .filter((s) => s.key !== 'price')
    .map((s) => (s.key === 'services' && priceStep ? { ...s, done: s.done && priceStep.done } : s));
  const moderation = readiness.data?.moderation ?? fromStatus?.moderation ?? null;

  return {
    steps,
    moderation,
    readyToSubmit: Boolean(readiness.data?.ready_to_submit ?? fromStatus?.ready_to_submit),
    comment: verification.verificationComment,
  };
}

export function TutorStepBar({
  current,
  onChange,
}: {
  current: string;
  onChange: (key: string) => void;
}) {
  const { steps, moderation, comment } = useTutorQuestionnaire();
  const fact = moderation ? FACT[moderation.status] : null;

  return (
    <div className={classes.bar}>
      {moderation && moderation.status !== 'draft' && fact ? (
        <div className={classes.status}>
          <StatusChip label={moderation.label} tone='blue' />
          <p>{comment && moderation.status !== 'approved' ? comment : fact}</p>
        </div>
      ) : null}
      <div className={classes.steps} role='tablist' aria-label='Шаги анкеты'>
        {steps.map((step) => {
          const active = step.key === current;
          return (
            <button
              key={step.key}
              type='button'
              role='tab'
              aria-selected={active}
              className={active ? classes.stepOn : classes.step}
              onClick={() => onChange(step.key)}
            >
              {TAB[step.key] ?? step.title}
            </button>
          );
        })}
      </div>
    </div>
  );
}

type DraftSignal = {
  field?: string;
  change_type?: string;
  after?: string | null;
  after_url?: string | null;
  field_changes?: { field?: string; change_type?: string }[];
};

function filledText(value: unknown): boolean {
  return typeof value === 'string' && value.trim().length > 0;
}

function meaningfulDraftAfter(value?: string | null): boolean {
  const text = (value ?? '')
    .trim()
    .replace(/[\u2014\u2013]/g, '-');
  if (!text || text === '-' || text === 'не загружено' || text === 'нет') return false;
  return true;
}

function formStepFilled(key: string, values?: TeacherEditFormValues | null): boolean {
  if (!values) return false;
  if (key === 'general') {
    return filledText(values.name) && filledText(values.surname) && filledText(values.birthDate);
  }
  if (key === 'education') {
    return values.educations.some((item) => {
      const photo = item.eduPhoto;
      const hasPhoto =
        (typeof File !== 'undefined' && photo instanceof File) ||
        filledText(item.token) ||
        (typeof photo === 'string' && filledText(photo));
      return filledText(item.university) && hasPhoto;
    });
  }
  if (key === 'services') {
    return values.teacher_prices.some((item) => {
      const direction = (item.service || '').trim();
      const price = Number(String(item.price ?? '').replace(/\s/g, '').replace(',', '.'));
      return Boolean(direction) && Number.isFinite(price) && price > 0;
    });
  }
  if (key === 'photo') {
    const photo = values.profilePhoto;
    return (
      (typeof File !== 'undefined' && photo instanceof File) ||
      (typeof photo === 'string' && photo.trim().length > 0)
    );
  }
  return false;
}

function draftStepFilled(key: string, rows: DraftSignal[]): boolean {
  const active = rows.filter((row) => row.change_type !== 'removed');
  if (key === 'general') {
    const name = active.some(
      (row) =>
        ['full_name', 'name', 'surname'].includes(row.field || '') &&
        meaningfulDraftAfter(row.after),
    );
    const birth = active.some(
      (row) => row.field === 'date_of_birth' && meaningfulDraftAfter(row.after),
    );
    return name && birth;
  }
  if (key === 'education') {
    return active.some((row) => {
      if (!['education', 'teacher_educations'].includes(row.field || '')) return false;
      if (!meaningfulDraftAfter(row.after)) return false;
      const changes = row.field_changes || [];
      if (changes.length) {
        const hasUniversity = changes.some((item) => item.field === 'university');
        const hasDiploma = changes.some(
          (item) => item.field === 'diploma_photo' && item.change_type !== 'removed',
        );
        return hasUniversity && hasDiploma;
      }
      return Boolean(row.after_url);
    });
  }
  if (key === 'services') {
    return active.some((row) => {
      if (row.field === 'teacher_prices') return meaningfulDraftAfter(row.after);
      if (!['service', 'category', 'subcategory', 'price'].includes(row.field || '')) return false;
      const changes = row.field_changes || [];
      if (changes.length) {
        const hasDirection = changes.some(
          (item) =>
            (item.field === 'category' || item.field === 'subcategory') &&
            item.change_type !== 'removed',
        );
        const hasPrice = changes.some(
          (item) => item.field === 'price' && item.change_type !== 'removed',
        );
        return hasDirection && hasPrice;
      }
      return /₽|руб/i.test(row.after || '') && meaningfulDraftAfter(row.after);
    });
  }
  if (key === 'photo') {
    return active.some((row) => {
      if (!['profile_photo', 'profilePhoto', 'profile_photo_token'].includes(row.field || '')) {
        return false;
      }
      return row.after === 'загружено' || Boolean(row.after_url) || row.after === 'новое фото';
    });
  }
  return false;
}

export function reviewStepDone(
  step: TutorStep,
  values?: TeacherEditFormValues | null,
  draft: DraftSignal[] = [],
): boolean {
  return step.done || formStepFilled(step.key, values) || draftStepFilled(step.key, draft);
}

export function TutorReviewPane({
  localChanges = [],
  values = null,
}: {
  localChanges?: ProfileChangeRow[];
  values?: TeacherEditFormValues | null;
}) {
  const { steps, readyToSubmit } = useTutorQuestionnaire();
  const verification = useVerificationEdits();
  const sent = verification.draftChanges || [];
  const rows = steps
    .filter((step) => step.key !== 'review')
    .map((step) => ({
      ...step,
      done: reviewStepDone(step, values, sent),
    }));
  const reviewReady = rows.every((step) => step.done) || readyToSubmit;

  return (
    <StepBody stepKey='tutor-review'>
      <div className={classes.review}>
        <h2>Проверьте анкету перед отправкой</h2>
        <p>
          Отправка передает профиль на проверку. Одобрение еще не получено.
        </p>
        {rows.map((step) => (
          <div key={step.key} className={classes.check}>
            <span className={classes.mark} aria-hidden='true'>
              {step.done ? '+' : '!'}
            </span>
            <div className={classes.checkCopy}>
              <p className={classes.checkTitle}>
                {TAB[step.key] ?? step.title}: {step.done ? 'заполнено' : 'пока пусто'}
              </p>
            </div>
          </div>
        ))}
        <div className={classes.row}>
          <p className={classes.rowLabel}>Документы и доступ</p>
          <p className={classes.rowValue}>
            Публичная карточка, поиск и чаты недоступны до одобрения
          </p>
        </div>
        <div className={classes.note}>
          <p className={classes.noteTitle}>После отправки</p>
          <CompactText className={classes.noteText} tone={'info'}>
            Профиль проверяют. Срок проверки не обещан.
          </CompactText>
        </div>
        <p>
          {reviewReady
            ? 'Анкета собрана. Отправьте ее на проверку.'
            : 'Заполните пустые шаги. Отправить анкету можно, когда они будут готовы.'}
        </p>
        <ProfileChangeList
          title="Что изменится"
          rows={localChanges}
          empty={
            sent.length
              ? undefined
              : 'Пока нет новых правок. Измените поле на предыдущих шагах - здесь появится сравнение.'
          }
        />
        {sent.length ? <ProfileChangeList title="Уже на проверке" rows={sent} /> : null}
      </div>
    </StepBody>
  );
}

export function nextTutorStep(steps: TutorStep[], current: string): string | null {
  const index = steps.findIndex((step) => step.key === current);
  if (index < 0) return steps[0]?.key ?? null;
  return steps[index + 1]?.key ?? null;
}
